<template>
  <div>
    <material-card>
      <v-toolbar slot="header" color="primary">
        <v-toolbar-title>Npos</v-toolbar-title>
        <v-spacer></v-spacer>
        <search-bar
          :searchColumns="filteredSearchColumn"
          @search-query="getCampaignNpos"
        ></search-bar>
        <upload-form
          v-if="uploadEnabled"
          :uploadFieldLabel="'select Npo CSV'"
          @file-submitted="uploadCsv"
        ></upload-form>
        <v-divider v-if="newNpoEnabled" class="mx-4" inset vertical></v-divider>
        <v-btn
          v-if="newNpoEnabled"
          class="ma-2"
          outlined
          color="white"
          @click.stop="rightDrawer = !rightDrawer"
          >New Npo</v-btn
        >
        <v-divider
          v-if="associateNpoVisible"
          class="mx-4"
          inset
          vertical
        ></v-divider>
        <v-btn
          class="ma-2"
          outlined
          color="white"
          :disabled="!associateNpoEnabled"
          v-if="associateNpoVisible"
          @click="getNpos"
          >Associate Npo</v-btn
        >
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="nposTable.data"
        :options.sync="nposTableOptions"
        :loading="nposTable.loading"
        class="elevation-1"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 30, 50],
        }"
        :items-per-page="itemsPerPage"
      >
        <template slot="headerCell" slot-scope="{ header }">
          <span
            class="font-weight-light text-warning text--darken-3"
            v-text="header.text"
          />
        </template>
        <template v-slot:item.name="{ item }">
          <span v-if="!canEdit">{{ item.name }}</span>
          <a v-if="canEdit" @click="goToNpo(item)" dark>{{ item.name }}</a>
        </template>
        <template v-slot:item.status_name="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark>{{
            item.status_name
          }}</v-chip>
        </template>
        <template v-slot:item.date_created="{ item }">
          <td>{{ item.date_created | formatDateSmall }}</td>
        </template>
        <template v-slot:item.action="{ item }">
          <v-icon small class="mr-2" @click="goToNpo(item)"
            >mdi-square-edit-outline</v-icon
          >
          <v-icon small @click="disassociateNpoDialog(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </material-card>

    <v-dialog v-model="nposDisassociateDialog" scrollable max-width="400px">
      <v-card>
        <v-card-title>Disassociate Npo</v-card-title>
        <v-card-text>
          Do you really want to disassociate Npo {{ disassociatedNpo.name }} ?
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="secondary darken-1"
            outlined
            @click="nposDisassociateDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="primary darken-1"
            @click="
              disassociateNpo(disassociatedNpo.id);
              nposDisassociateDialog = false;
            "
            >Disassociate Npo</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="nposDialog" modal max-width="400px">
      <v-card>
        <v-card-title>Associate Npo</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form ref="npoAssociation" v-model="npoAssociationForm.valid">
            <v-combobox
              v-model="associatedNpo"
              :items="availableNpoList"
              label="Npo"
              item-value="npo_id"
              item-text="name"
              clearable
              deletable-chips
              :rules="[(v) => !!v || 'Npo is required']"
            ></v-combobox>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="secondary darken-1" outlined @click="nposDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary darken-1" @click="associateNpo(associatedNpo)"
            >Associate Npos</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiService from '@/services'
import searchBar from '@/components/common-components/ComponentSearchBar'
import uploadForm from '@/components/npos/ComponentUpload'
import { mapState } from 'vuex'
import { tablePaginationMixin } from '@/mixins/tablePaginationMixin'
import { statusColor } from '@/mixins/statusColor'

export default {
  props: {
    uploadEnabled: {
      type: Boolean,
      default: true
    },
    newNpoEnabled: {
      type: Boolean,
      default: true
    },
    associateNpoVisible: {
      type: Boolean,
      default: true
    },
    associateNpoEnabled: {
      type: Boolean,
      default: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    upload: {
      type: Boolean,
      default: true
    },
    campaign: {
      type: String,
      required: false
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 15
    },
    headers: {
      type: Array,
      required: false,
      default () {
        return [
          {
            text: 'Npo',
            align: 'left',
            value: 'name',
            sortable: true,
            searchable: true
          },
          {
            text: 'Fiscal Code',
            value: 'fiscal_code',
            sortable: true,
            searchable: true
          },
          {
            text: 'Contact',
            align: 'left',
            value: 'contact',
            sortable: true,
            searchable: true
          },
          {
            text: 'Intervention area',
            value: 'type',
            sortable: true,
            searchable: true
          },
          {
            text: 'Campaign',
            value: 'campaign',
            sortable: true,
            searchable: true
          },
          {
            text: 'Registration status',
            value: 'registration_status_name',
            sortable: true,
            searchable: true
          },
          {
            text: 'Status',
            value: 'status_name',
            sortable: true,
            searchable: true
          },
          {
            text: 'City',
            value: 'city',
            sortable: true,
            searchable: true
          },
          {
            text: 'Cap',
            value: 'zip_code',
            sortable: true,
            searchable: true
          },
          {
            text: 'Address',
            value: 'address',
            sortable: true,
            searchable: true
          },
          {
            text: 'Actions',
            value: 'action',
            sortable: false
          }
        ]
      }
    }
  },
  components: {
    searchBar,
    uploadForm
  },
  mixins: [tablePaginationMixin, statusColor],
  watch: {
    nposTableOptions: {
      handler () {
        this.getCampaignNpos()
      },
      deep: true
    }
  },

  data () {
    return {
      nposDialog: false,
      nposDisassociateDialog: false,
      associatedNpo: [],
      disassociatedNpo: {},
      availableNpoList: [],
      npoList: [],
      npoAssociationForm: {
        valid: false
      },

      rightDrawer: false,
      tableExpanded: [],

      file: null,

      nposTable: {
        data: [],
        total: 0,
        loading: true,
        search: '',
        column_search: ''
      },
      nposTableOptions: {}
    }
  },

  computed: {
    filteredSearchColumn () {
      // Table headers are also used for search porpuses
      // Filter out unsearchable headers
      return this.headers.filter((item) => item.searchable !== false)
    },

    ...mapState({
      auth: 'auth',
      alerts: (state) => state.alert.snackbars
    }),

    loggedIn () {
      return this.auth.loggedIn
    },

    loggedUser () {
      return this.auth.user || null
    },

    // Get general entity statuses from store
    ...mapState('init', ['statuses', 'interventionAreas'])
  },

  methods: {
    // Get Npos associated with the campaign, this will populte the Npos table in campaign detail
    getCampaignNpos (searchParams = {}) {
      this.nposTable.loading = true
      // Hide rigth drawer
      this.rightDrawer = false

      const { sortBy, sortDesc, page, itemsPerPage } = this.nposTableOptions
      const campaignId = this.$route.params.campaignId

      let filter = {}
      filter.orderBy = sortBy[0] || null
      filter.orderDesc = sortDesc[0] || null
      filter.page = page
      filter.limit = itemsPerPage

      filter.search_filter =
        !!searchParams && searchParams.tableSearch !== ''
          ? searchParams.searchColumn
          : ''
      filter.search_value = searchParams.tableSearch || ''

      apiService
        .getCampaignNpos(campaignId, filter)
        .then((response) => {
          //  Take only email of the tech rep
          this.nposTable.data = response.map((el) => {
            return {
              ...el,
              area: el.area.map((e) => e.name)
            }
          })
          this.nposTable.total = response.length
          this.nposTable.loading = false
        })
        .catch(() => {
          this.nposTable.loading = false
          this.$store.dispatch('alert/showSnackbar', {
            color: 'error',
            text: 'Unable to get campaign Npos'
          })
        })
    },

    uploadCsv (formData) {
      apiService.uploadNpos(formData).then((response) => {
        this.$store.dispatch('alert/showSnackbar', {
          color: 'success',
          text: `CSV Succesfully uploaded`
        })
        this.getCampaignNpos()
      })
    },

    // Obtain Available Npos for association
    getNpos () {
      apiService
        .fetchData('npos')
        .then((response) => {
          const totalNposList = response
          const onlyInA = totalNposList.filter((obj) => {
            return (
              this.nposTable.data.filter((other) => {
                return other.id === obj.id
              }).length === 0
            )
          })

          this.totalNposList = onlyInA
          this.availableNpoList = onlyInA.filter((npo) => {
            return npo.status === 1
          })
          this.nposDialog = true
        })
        .catch((error) => {
          console.log(error)
        })
    },

    associateNpo (npo) {
      console.log(npo)
      if (npo.status === 2) {
        this.$store.dispatch('alert/showSnackbar', {
          color: 'error',
          text: `Npo must be active to be associated`
        })
        return
      }
      const a = []
      const data = {
        npo_ids: a,
        npo_id: npo.id,
        campaign_id: this.$route.params.campaignId
      }
      apiService
        .associateNpo(data)
        .then((response) => {
          this.getCampaignNpos()
        })
        .catch(() => {
          this.$store.dispatch('alert/showSnackbar', {
            color: 'error',
            text: `Cannot associate Npo`
          })
        })

      this.nposDialog = false
      this.$refs.npoAssociation.reset()
    },

    /**
     * Navigation to Npo detail
     */
    goToNpo (item) {
      this.$router.push({ name: 'npodetail', params: { npoId: item.id } })
    },

    /**
     *  Open disassociation dialog
     */
    disassociateNpoDialog (npo) {
      this.disassociatedNpo = npo
      this.nposDisassociateDialog = true
    },

    /**
     * Disassociate Npo from campaign
     */
    disassociateNpo (npoId) {
      const params = {
        npo_id: npoId,
        campaign_id: this.$route.params.campaignId
      }

      apiService
        .disassociateNpo(params)
        .then((response) => {
          this.$store.dispatch('alert/showSnackbar', {
            color: 'success',
            text: `Npo succesfully disassociated`
          })
          this.getCampaignNpos()
        })
        .catch((error) => {
          console.log(error)
          this.$store.dispatch('alert/showSnackbar', {
            color: 'error',
            text: `Cannot disassociate Npo`
          })
        })
    }
  }
}
</script>
